import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";

export interface StatisticProps {
  title?: ReactNode;
  value: number | string;
  precision?: number;
  prefix?: ReactNode;
  suffix?: ReactNode;
  /** Override the rendered value (e.g. locale-aware number formatting). */
  formatter?: (value: number | string) => ReactNode;
  valueStyle?: CSSProperties;
  className?: string;
}

export function Statistic({
  title,
  value,
  precision,
  prefix,
  suffix,
  formatter,
  valueStyle,
  className,
}: StatisticProps) {
  const shown: ReactNode = formatter
    ? formatter(value)
    : precision != null && typeof value === "number"
      ? value.toFixed(precision)
      : value;
  return (
    <div className={className}>
      {title != null && <div className="text-sm text-muted-foreground">{title}</div>}
      <div
        className="flex items-baseline gap-1 text-2xl font-semibold tabular-nums text-foreground"
        style={valueStyle}
      >
        {prefix != null && <span className="text-base font-normal text-muted-foreground">{prefix}</span>}
        <span>{shown}</span>
        {suffix != null && <span className="text-base font-normal text-muted-foreground">{suffix}</span>}
      </div>
    </div>
  );
}

export interface CountdownProps {
  title?: ReactNode;
  /** Target timestamp in ms (e.g. Date.now() + 60_000). */
  value: number;
  onFinish?: () => void;
  prefix?: ReactNode;
  suffix?: ReactNode;
  className?: string;
}

function fmt(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

export function Countdown({ title, value, onFinish, prefix, suffix, className }: CountdownProps) {
  const [now, setNow] = useState(() => Date.now());
  const done = useRef(false);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const remaining = value - now;
  useEffect(() => {
    if (remaining <= 0 && !done.current) {
      done.current = true;
      onFinish?.();
    }
  }, [remaining, onFinish]);

  return (
    <div className={className}>
      {title != null && <div className="text-sm text-muted-foreground">{title}</div>}
      <div className="flex items-baseline gap-1 text-2xl font-semibold tabular-nums text-foreground">
        {prefix}
        <span>{fmt(remaining)}</span>
        {suffix}
      </div>
    </div>
  );
}
